import { datadogLogs } from '@datadog/browser-logs';
import { client } from '@antimatterhq/asl-api-ts-v1';
import { AxiosRequestConfig } from 'axios';

interface RequestConfigWithMetadata extends AxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

export const setupInterceptors = () => {
  client.instance.interceptors.request.use((config) => {
    // eslint-disable-next-line no-param-reassign
    (config as RequestConfigWithMetadata).metadata = {
      startTime: new Date().getTime(),
    }; // Add start time to request config
    return config;
  });

  client.instance.interceptors.response.use(
    (response) => {
      const endTime = new Date().getTime();

      const { status, statusText, data, request } = response;
      const {
        env,
        adapter,
        signal,
        transformRequest,
        transformResponse,
        transitional,
        ...config
      } = response.config;

      const configWithMetadata = response.config as RequestConfigWithMetadata;
      const duration = configWithMetadata.metadata?.startTime
        ? endTime - configWithMetadata.metadata.startTime
        : 0;

      const method = config.method?.toUpperCase();
      const url = request.responseURL;

      const payload = {
        config,
        response: {
          status,
          statusText,
          data,
          duration,
        },
        request: { method, url },
      };

      if (duration > 5000) {
        datadogLogs.logger.warn(`${status} ${method} (SLOW) ${url}`, payload);
      } else {
        datadogLogs.logger.info(`${status} ${method} ${url}`, payload);
      }

      return response;
    },
    (error) => {
      if (error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }

      let statusText = '';
      let status = 0;
      let data = {};

      if (error.response) {
        status = error.response.status;
        statusText = error.response.statusText;
        data = error.response.data;
      } else if (error.code === 'ERR_NETWORK') {
        status = 400;
        statusText = 'Network Error';
      } else if (
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_TIMEOUT'
      ) {
        status = 408;
        statusText = 'Request Timeout';
      } else if (error.code === 'ERR_BAD_REQUEST') {
        status = 400;
        statusText = 'Bad Request';
      } else if (error.code === 'ERR_FORBIDDEN') {
        status = 403;
        statusText = 'Forbidden';
      } else if (error.code === 'ERR_BAD_RESPONSE') {
        status = 500;
        statusText = 'Bad Response';
      } else if (
        error.code === 'ERR_BAD_OPTION' ||
        error.code === 'ERR_BAD_OPTION_VALUE'
      ) {
        status = 500;
        statusText = 'Bad Option Configuration';
      } else {
        // Fallback for any other error types
        status = 500;
        statusText = error.message || 'Unknown Error';
      }

      const {
        client: cli,
        env,
        adapter,
        signal,
        transformRequest,
        transformResponse,
        transitional,
        ...config
      } = error.config;
      const method = config.method.toUpperCase();
      const url = config.url;
      datadogLogs.logger.error(
        `${status} ${method} ${url}`,
        {
          config,
          response: { status, statusText, data, code: error.code },
          request: { method, url },
        },
        error,
      );
      return Promise.reject(error);
    },
  );
};
